import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { MonthlyKmRankingDashboardDto } from 'api/dashboard/dtos/MonthlyKmRankingDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useInfiniteReveal from '../components/useInfiniteReveal';
import useWidgetFilters from '../hooks/useWidgetFilters';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import KpiHeader from '../components/KpiHeader';
import { getPreviousRange, PreviousRange } from '../utils/getPreviousRange';
import styles from './MonthlyKmRanking.module.scss';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const PAGE_SIZE = 10;

const SCALE_COLORS = [
	'#025a7a', // darkest — top rank
	'#038cb6',
	'#02c7c7',
	'#6de0e0',
	'#b8f0f0', // lightest — 5th+
];

type Props = {
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const formatKm = (value: number) => {
	return Math.round(value).toLocaleString('fr-FR') + ' km';
};

const MonthlyKmRanking = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<MonthlyKmRankingDashboardDto[] | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [previousTotal, setPreviousTotal] = useState<number | null>(null);
	const [previousRange, setPreviousRange] = useState<PreviousRange | null>(null);
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('monthlyKmRanking');

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			setPreviousTotal(null);
			setPreviousRange(null);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom, dateTo };
				const result = await DashboardService.getMonthlyKmRanking(criteria);
				if (!cancelled) setData(result);
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}

			try {
				const range = getPreviousRange(dateFrom, dateTo);
				const prevCriteria = props.filters
					? { ...props.filters, dateFrom: range.prevFrom, dateTo: range.prevTo }
					: { dateFrom: range.prevFrom, dateTo: range.prevTo };
				const previous = await DashboardService.getMonthlyKmRanking(prevCriteria);
				if (!cancelled) {
					setPreviousTotal((previous || []).reduce((s, x) => s + x.totalKm, 0));
					setPreviousRange(range);
				}
			} catch {
				if (!cancelled) {
					setPreviousTotal(null);
					setPreviousRange(null);
				}
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, dateFrom, dateTo]);

	const items = data || [];
	const maxKm = Math.max(...items.map(d => d.totalKm), 0);
	const totalFleetKm = items.reduce((sum, item) => sum + item.totalKm, 0);
	const { visibleItems, sentinelRef, hasMore } = useInfiniteReveal(items, PAGE_SIZE);

	const buildVehicleTooltip = (item: MonthlyKmRankingDashboardDto) =>
		[item.vehicleBrandName, item.vehicleModelName].filter(Boolean).join(' ') || undefined;

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.monthly_km_ranking' as any) as string}
			subTitleTooltip={t('dashboard.monthly_km_ranking_tooltip' as any) as string}
			renderOptions={<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}
		>
			{isLoading ? (
				<div className={tableStyles.centerAlign}><LoadingSpinner /></div>
			) : !data || data.length === 0 ? (
				<WidgetNoData />
			) : (
				<>
					<KpiHeader
						value={formatKm(totalFleetKm)}
						subLabel={t('dashboard.total_km' as any) as string}
						currentTotal={totalFleetKm}
						previousTotal={previousTotal}
						previousRange={previousRange}
						lowerIsBetter={false}
					/>
					<div className={styles.list}>
					{visibleItems.map((item, index) => {
						const barWidth = maxKm > 0 ? (item.totalKm / maxKm) * 100 : 0;
						const pct = totalFleetKm > 0 ? Math.round((item.totalKm / totalFleetKm) * 100) : 0;
						const color = SCALE_COLORS[Math.min(index, SCALE_COLORS.length - 1)];
						return (
							<div
								key={item.vehicleId}
								className={styles.row}
								onClick={() => window.open(`/vehicles/details/${item.vehicleId}/mileage`, '_blank')}
							>
								<span className={styles.plate}><span data-tip={buildVehicleTooltip(item)} data-for="monthly-km-ranking-tooltip" data-place="right">{item.registrationNumber}</span></span>
								<div className={styles.barTrack}>
									<div className={styles.barFill} style={{ width: `${barWidth}%`, background: color }} />
								</div>
								<span className={styles.km}>
									{formatKm(item.totalKm)}
								</span>
								<span className={styles.pct}>{pct}%</span>
							</div>
						);
					})}
					{hasMore && <div ref={sentinelRef} className={infiniteStyles.sentinel} />}
					</div>
					<ReactTooltip id="monthly-km-ranking-tooltip" className={styles.tooltip} effect="solid" multiline={true} />
				</>
			)}
		</Box>
	);
};

export default MonthlyKmRanking;
